<script setup lang="ts">
import { computed, ref } from 'vue'
import MaxWidthWrapper from '@/components/MaxWidthWrapper.vue'
import { useDisplay } from 'vuetify/framework'
import { useInstanceStore } from '@/store/backendInstances'
import CameraPrivacyDialog from '@/components/CameraPrivacyDialog.vue'
import DashboardCanvas from '@/components/dashboard-overview/DashboardCanvas.vue'
import DashboardTimelapse from '@/components/dashboard-overview/DashboardTimelapse.vue'
import { canViewCanvasTimelapse } from '@/composables/permissionHelper'
import ExpandableCard from '@/components/ExpandableCard.vue'
import GenericInfluxChart from '@/composables/charts/GenericInfluxChart.vue'
import { RegionInfluxChartBuilder } from '@/composables/charts/influx-charts/chart-builders/RegionChartBuilder'
import { CHART_TYPES } from '@/composables/charts/influx-charts/chart-builders/CHART_TYPES'
import { GeneralChartTimeSetup } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup'
import { RegionSumInfluxChartBuilder } from '@/composables/charts/influx-charts/chart-builders/RegionSumChartBuilder'
import { ANNOTATOR_TYPES } from '@/composables/charts/influx-charts/ChartIncidentAnnotators'
import { IRInfluxChartBuilder } from '@/composables/charts/influx-charts/chart-builders/IRChartBuilder'
const incidentAnnotators = [{ annotatorType: ANNOTATOR_TYPES.BARNDOC }, { annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY }]

const { mobile } = useDisplay()
const instanceStore = useInstanceStore()
const showPrivacyDialog = ref(false)
const allStandingPigs = ['piglet_standing', 'pig_standing', 'sow_standing']
const allNonStandingPigs = [
  'piglet',
  'piglet_lying',
  'piglet_sitting',
  'pig',
  'pig_sitting',
  'pig_lying',
  'sow_lying_belly',
  'sow_lying_side',
  'sow_sitting',
]

const hasPrivacyPermission = computed(() => {
  return instanceStore.hasPermission('privacy.change')
})
const canViewCanvas = computed(() => {
  return instanceStore.hasAnyPermission(['canvas.view', 'cameraCanvases.view', 'cameraCanvases.view.basic'])
})
const canViewTimelapse = computed(() => {
  return canViewCanvasTimelapse()
})
const canSeeImageQualityData = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.imageQuality'])
})
</script>

<template>
  <MaxWidthWrapper :container-class="mobile ? 'py-4 d-flex flex-column ga-2' : 'py-6 d-flex flex-column ga-4'">
    <div
      v-if="hasPrivacyPermission"
      class="my-2"
    >
      <v-btn
        class="w-100"
        color="primary"
        variant="elevated"
        prepend-icon="mdi-cctv-off"
        :text="$t('general_interface.buttons.privacy')"
        @click="showPrivacyDialog = true"
      />

      <CameraPrivacyDialog v-model:show-privacy-dialog="showPrivacyDialog" />
    </div>

    <div
      v-if="canViewCanvas"
      class="my-2"
    >
      <DashboardCanvas />
    </div>

    <div
      v-if="canViewTimelapse"
      class="my-2"
    >
      <ExpandableCard :model-value="false">
        <template #title>
          <v-card-title>{{ $t('artifact.type.canvasTimelapse') }}</v-card-title>
        </template>
        <template #text>
          <DashboardTimelapse />
        </template>
      </ExpandableCard>
    </div>

    <v-divider class="my-4"></v-divider>

    <h2>{{ $t('dashboards.fattening.standing_animals_12h') }}</h2>
    <GenericInfluxChart
      :chart-builder="
        new RegionInfluxChartBuilder({
          xAggregation: 'mean',
          regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['fuetterung'] },
          chartType: CHART_TYPES.REGIONS,
          regionType: 'stalltechnik',
          groupByTags: ['pen'],
          whereClauses: { label: allStandingPigs },
          title: $t('charts.titles.regions'),
          subtitle: $t('charts.subtitles.regionsBarnEquipment_standing_pigs'),
          timeSetup: GeneralChartTimeSetup.lastNDays(7, 'fatteningOverview', 1000 * 60 * 60 * 12).serialize(),
          incidentAnnotators: incidentAnnotators,
          incidentReducer: { reducerType: 'penState' },
        })
      "
    ></GenericInfluxChart>

    <GenericInfluxChart
      :chart-builder="
        new RegionInfluxChartBuilder({
          xAggregation: 'mean',
          regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['traenke'] },
          chartType: CHART_TYPES.REGIONS,
          regionType: 'stalltechnik',
          groupByTags: ['pen'],
          whereClauses: { label: allStandingPigs },
          title: $t('charts.titles.regions'),
          subtitle: $t('charts.subtitles.regionsBarnEquipment_standing_pigs'),
          timeSetup: GeneralChartTimeSetup.lastNDays(7, 'fatteningOverview', 1000 * 60 * 60 * 12).serialize(),
          incidentAnnotators: incidentAnnotators,
          incidentReducer: { reducerType: 'penState' },
        })
      "
    ></GenericInfluxChart>

    <v-divider class="my-4"></v-divider>
    <h2>{{ $t('dashboards.fattening.lying_animals_12h') }}</h2>

    <GenericInfluxChart
      :chart-builder="
        new RegionInfluxChartBuilder({
          xAggregation: 'mean',
          regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['fuetterung'] },
          chartType: CHART_TYPES.REGIONS,
          regionType: 'stalltechnik',
          groupByTags: ['pen'],
          whereClauses: { label: allNonStandingPigs },
          title: $t('charts.titles.regions'),
          subtitle: $t('charts.subtitles.regionsBarnEquipment_non_standing_pigs'),
          timeSetup: GeneralChartTimeSetup.lastNDays(7, 'fatteningOverview', 1000 * 60 * 60 * 12).serialize(),
          incidentAnnotators: incidentAnnotators,
          incidentReducer: { reducerType: 'penState' },
        })
      "
    ></GenericInfluxChart>

    <GenericInfluxChart
      :chart-builder="
        new RegionInfluxChartBuilder({
          xAggregation: 'mean',
          regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['traenke'] },
          chartType: CHART_TYPES.REGIONS,
          regionType: 'stalltechnik',
          groupByTags: ['pen'],
          whereClauses: { label: allNonStandingPigs },
          title: $t('charts.titles.regions'),
          subtitle: $t('charts.subtitles.regionsBarnEquipment_non_standing_pigs'),
          timeSetup: GeneralChartTimeSetup.lastNDays(7, 'fatteningOverview', 1000 * 60 * 60 * 12).serialize(),
          incidentAnnotators: incidentAnnotators,
          incidentReducer: { reducerType: 'penState' },
        })
      "
    ></GenericInfluxChart>

    <v-divider class="my-4"></v-divider>

    <GenericInfluxChart
      :chart-builder="
        new RegionSumInfluxChartBuilder({
          chartType: CHART_TYPES.REGION_SUMS,
          field: 'meanGridScore',
          timeSetup: GeneralChartTimeSetup.lastNDays(7, 'fatteningOverview', 1000 * 60 * 60 * 12).serialize(),
          title: $t('charts.titles.gridScore'),
          subtitle: $t('charts.subtitles.gridScore'),
          defaultCollapsed: false,
          xAggregation: 'mean',
          regionSumProperty: 'pen',
          regionSumValueTagSelector: { tag: 'value', allowEmptyValues: false, groupByValues: true, useAll: false },
          incidentAnnotators: incidentAnnotators,
          incidentReducer: { reducerType: 'penState' },
        })
      "
    ></GenericInfluxChart>

    <GenericInfluxChart
      :chart-builder="
        new RegionSumInfluxChartBuilder({
          chartType: CHART_TYPES.REGION_SUMS,
          field: 'meanSpeed',
          timeSetup: GeneralChartTimeSetup.lastNDays(7, 'fatteningOverview', 1000 * 60 * 60 * 12).serialize(),
          title: $t('charts.titles.speed'),
          subtitle: $t('charts.subtitles.speedPerPen'),
          defaultCollapsed: false,
          xAggregation: 'mean',
          regionSumProperty: 'pen',
          regionSumValueTagSelector: { tag: 'value', allowEmptyValues: false, groupByValues: true, useAll: false },
          incidentAnnotators: incidentAnnotators,
          incidentReducer: { reducerType: 'penState' },
        })
      "
    ></GenericInfluxChart>

    <GenericInfluxChart
      v-if="canSeeImageQualityData"
      :chart-builder="
        new IRInfluxChartBuilder({
          timeSetup: GeneralChartTimeSetup.lastNDays(7, 'fatteningOverview', 1000 * 60).serialize(),
          chartType: CHART_TYPES.IR,
          seriesBasedHeight: true,
          xAggregation: 'first',
          cameraTagSelector: { tag: 'camera', allowEmptyValues: false, groupByValues: true, useAll: true },
          title: $t('charts.titles.nightvision_range_bar'),
        })
      "
    ></GenericInfluxChart>

    <v-divider class="my-4"></v-divider>

    <h2>{{ $t('dashboards.fattening.standing_animals_30min') }}</h2>
    <GenericInfluxChart
      :chart-builder="
        new RegionInfluxChartBuilder({
          xAggregation: 'mean',
          regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['fuetterung'] },
          chartType: CHART_TYPES.REGIONS,
          regionType: 'stalltechnik',
          groupByTags: ['pen'],
          whereClauses: { label: allStandingPigs },
          title: $t('charts.titles.regions'),
          subtitle: $t('charts.subtitles.regionsBarnEquipment_standing_pigs'),
          timeSetup: GeneralChartTimeSetup.lastNDays(7, 'fatteningOverview', 1000 * 60 * 30).serialize(),
          incidentAnnotators: incidentAnnotators,
          incidentReducer: { reducerType: 'penState' },
        })
      "
    ></GenericInfluxChart>

    <GenericInfluxChart
      :chart-builder="
        new RegionInfluxChartBuilder({
          xAggregation: 'mean',
          regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['traenke'] },
          chartType: CHART_TYPES.REGIONS,
          regionType: 'stalltechnik',
          groupByTags: ['pen'],
          whereClauses: { label: allStandingPigs },
          title: $t('charts.titles.regions'),
          subtitle: $t('charts.subtitles.regionsBarnEquipment_standing_pigs'),
          timeSetup: GeneralChartTimeSetup.lastNDays(7, 'fatteningOverview', 1000 * 60 * 30).serialize(),
          incidentAnnotators: incidentAnnotators,
          incidentReducer: { reducerType: 'penState' },
        })
      "
    ></GenericInfluxChart>

    <v-divider class="my-4"></v-divider>
    <h2>{{ $t('dashboards.fattening.lying_animals_30min') }}</h2>

    <GenericInfluxChart
      :chart-builder="
        new RegionInfluxChartBuilder({
          xAggregation: 'mean',
          regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['fuetterung'] },
          chartType: CHART_TYPES.REGIONS,
          regionType: 'stalltechnik',
          groupByTags: ['pen'],
          whereClauses: { label: allNonStandingPigs },
          title: $t('charts.titles.regions'),
          subtitle: $t('charts.subtitles.regionsBarnEquipment_non_standing_pigs'),
          timeSetup: GeneralChartTimeSetup.lastNDays(7, 'fatteningOverview', 1000 * 60 * 30).serialize(),
          incidentAnnotators: incidentAnnotators,
          incidentReducer: { reducerType: 'penState' },
        })
      "
    ></GenericInfluxChart>

    <GenericInfluxChart
      :chart-builder="
        new RegionInfluxChartBuilder({
          xAggregation: 'mean',
          regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['traenke'] },
          chartType: CHART_TYPES.REGIONS,
          regionType: 'stalltechnik',
          groupByTags: ['pen'],
          whereClauses: { label: allNonStandingPigs },
          title: $t('charts.titles.regions'),
          subtitle: $t('charts.subtitles.regionsBarnEquipment_non_standing_pigs'),
          timeSetup: GeneralChartTimeSetup.lastNDays(7, 'fatteningOverview', 1000 * 60 * 30).serialize(),
          incidentAnnotators: incidentAnnotators,
          incidentReducer: { reducerType: 'penState' },
        })
      "
    ></GenericInfluxChart>

    <v-divider class="my-4"></v-divider>
    <GenericInfluxChart
      :chart-builder="
        new RegionSumInfluxChartBuilder({
          chartType: CHART_TYPES.REGION_SUMS,
          field: 'meanGridScore',
          timeSetup: GeneralChartTimeSetup.lastNDays(7, 'fatteningOverview', 1000 * 60 * 30).serialize(),
          title: $t('charts.titles.gridScore'),
          subtitle: $t('charts.subtitles.gridScore'),
          defaultCollapsed: false,
          xAggregation: 'mean',
          regionSumProperty: 'pen',
          regionSumValueTagSelector: { tag: 'value', allowEmptyValues: false, groupByValues: true, useAll: false },
          incidentAnnotators: incidentAnnotators,
          incidentReducer: { reducerType: 'penState' },
        })
      "
    ></GenericInfluxChart>

    <GenericInfluxChart
      :chart-builder="
        new RegionSumInfluxChartBuilder({
          chartType: CHART_TYPES.REGION_SUMS,
          field: 'meanSpeed',
          timeSetup: GeneralChartTimeSetup.lastNDays(7, 'fatteningOverview', 1000 * 60 * 30).serialize(),
          title: $t('charts.titles.speed'),
          subtitle: $t('charts.subtitles.speedPerPen'),
          defaultCollapsed: false,
          xAggregation: 'mean',
          regionSumProperty: 'pen',
          regionSumValueTagSelector: { tag: 'value', allowEmptyValues: false, groupByValues: true, useAll: false },
          incidentAnnotators: incidentAnnotators,
          incidentReducer: { reducerType: 'penState' },
        })
      "
    ></GenericInfluxChart>
  </MaxWidthWrapper>
</template>

<style scoped></style>
